import React from 'react';

const Gauge = ({level, checked}) => {
    const getRotation = () => {
        switch (level) {
            case 'BEGINNER':
                return -60;
            case 'PROFICIENT':
                return 0;
            case 'ADVANCED':
                return 60;
            default:
                return 0;
        }
    };

    const rotation = checked ? getRotation() : 0;

    return (
        <div className="flex flex-col items-center font-sans">
            <svg width="100" height="60" viewBox="0 0 100 60">
                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#E5E7EB" strokeWidth="10"/>
                <path d="M 10 50 A 40 40 0 0 1 30 17" fill="none" stroke="#EF4444" strokeWidth="10"/>
                <path d="M 30 17 A 40 40 0 0 1 70 17" fill="none" stroke="#FBBF24" strokeWidth="10"/>
                <path d="M 70 17 A 40 40 0 0 1 90 50" fill="none" stroke="#22C55E" strokeWidth="10"/>
                <line
                    x1="50" y1="50" x2="50" y2="15"
                    stroke="#374151" strokeWidth="3" strokeLinecap="round"
                    transform={`rotate(${rotation}, 50, 50)`}
                    style={{transition: 'transform 0.5s ease-in-out'}}
                />
                <circle cx="50" cy="50" r="5" fill="#374151"/>
            </svg>
            <span className="text-xs font-bold mt-1">{level}</span>
        </div>
    );
};


const DomainSection = ({domainData}) => {
    const getCheckedLevel = (ability) => {
        if (!ability) return null;
        if (ability.beginner) return 'BEGINNER';
        if (ability.proficient) return 'PROFICIENT';
        if (ability.advanced) return 'ADVANCED';
        return null;
    };

    const awarenessLevel = getCheckedLevel(domainData.awareness);
    const sensitivityLevel = getCheckedLevel(domainData.sensitivity);
    const creativityLevel = getCheckedLevel(domainData.creativity);

    const abilities = [
        {label: 'Awareness', data: domainData.awareness},
        {label: 'Sensitivity', data: domainData.sensitivity},
        {label: 'Creativity', data: domainData.creativity},
    ];

    return (
        <div className="mb-4 border border-gray-400">
            {/* Header: domain name (w-1/3) + gauges spanning full w-2/3 */}
            <div className="flex">
                <div className="w-1/3 bg-orange-500 text-white font-bold text-center py-2 flex items-center justify-center">
                    {domainData.domain_name}
                </div>
                <div className="w-2/3 border-l border-gray-400">
                    <div className="bg-orange-500 text-white font-bold text-center py-1 text-sm">
                        Performance Level Descriptors
                    </div>
                    {/* grid-cols-3: gauges fill the full right panel evenly */}
                    <div className="grid grid-cols-3 p-2 bg-white">
                        <Gauge level="BEGINNER"
                               checked={awarenessLevel === 'BEGINNER' || sensitivityLevel === 'BEGINNER' || creativityLevel === 'BEGINNER'}/>
                        <Gauge level="PROFICIENT"
                               checked={awarenessLevel === 'PROFICIENT' || sensitivityLevel === 'PROFICIENT' || creativityLevel === 'PROFICIENT'}/>
                        <Gauge level="ADVANCED"
                               checked={awarenessLevel === 'ADVANCED' || sensitivityLevel === 'ADVANCED' || creativityLevel === 'ADVANCED'}/>
                    </div>
                </div>
            </div>

            {/* Abilities: unified 3fr:2fr:2fr:2fr grid
                3fr = same width as w-1/3 panel above
                each 2fr = same width as one gauge column (1/3 of w-2/3)
                Result: checkboxes are pixel-perfect under their gauge */}
            <div className="border-t border-gray-400"
                 style={{display: 'grid', gridTemplateColumns: '3fr 2fr 2fr 2fr'}}>
                {abilities.map(({label, data}, index) => (
                    <React.Fragment key={label}>
                        <div className={`bg-white flex items-center text-sm px-2 py-2 border-r border-gray-400${index < 2 ? ' border-b border-gray-200' : ''}`}>
                            <span className="font-bold text-base mr-1">{label[0]}</span>{label.slice(1)}
                        </div>
                        <div className={`bg-orange-50 flex justify-center items-center py-2${index < 2 ? ' border-b border-gray-200' : ''}`}>
                            <input type="checkbox" className="w-4 h-4" readOnly checked={data?.beginner || false}/>
                        </div>
                        <div className={`bg-orange-50 flex justify-center items-center py-2${index < 2 ? ' border-b border-gray-200' : ''}`}>
                            <input type="checkbox" className="w-4 h-4" readOnly checked={data?.proficient || false}/>
                        </div>
                        <div className={`bg-orange-50 flex justify-center items-center py-2${index < 2 ? ' border-b border-gray-200' : ''}`}>
                            <input type="checkbox" className="w-4 h-4" readOnly checked={data?.advanced || false}/>
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {/* Observational Notes */}
            <div className="p-2 bg-white border-t border-gray-400">
                <p className="text-sm italic mb-1">{domainData.observational_notes || ''}</p>
                <div className="space-y-2.5">
                    <div className="border-b border-gray-400 h-1"></div>
                    <div className="border-b border-gray-400 h-1"></div>
                    <div className="border-b border-gray-400 h-1"></div>
                </div>
            </div>
        </div>
    );
};


const PartC = ({data}) => {
    if (!data || data.length === 0) {
        return (
            <div className="report-page flex items-center justify-center">
                Loading Part C Data...
            </div>
        );
    }

    const DOMAIN_ORDER = [
        "Language (R1)", "Language (R2)", "Mathematics", "The World Around Us", "Art Education", "Physical Education"
    ];

    const sortedData = [...data].sort((a, b) => {
        const indexA = DOMAIN_ORDER.indexOf(a.domain_name);
        const indexB = DOMAIN_ORDER.indexOf(b.domain_name);
        return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
    });

    const pages = [];
    for (let i = 0; i < sortedData.length; i += 3) {
        pages.push(sortedData.slice(i, i + 3));
    }

    return (
        <div className="report-page bg-white font-[poppins]" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.5',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {pages.map((pageDomains, pageIndex) => (
                <div key={pageIndex} className="report-page bg-white font-sans">
                    {pageIndex === 0 && (
                        <div className="text-center mb-4">
                            <h1 className="text-2xl font-bold text-orange-600">PART C</h1>
                            <h2 className="text-lg font-bold mb-2">SUMMARY FOR THE ACADEMIC YEAR</h2>
                            <p className="text-sm text-left">
                                Tick the appropriate performance level descriptor and write an observational note for
                                each category based on performance throughout the academic year.
                            </p>
                        </div>
                    )}
                    {pageDomains.map((domain, index) => (
                        <DomainSection key={domain.domain_id || `domain-${index}`} domainData={domain}/>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default PartC;
